import React from 'react';
import { Button } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { F21A1 } from '../../../services/pw220aApi';

interface TabDetailsProps {
  details: F21A1[];
  isEditing: boolean;
  onAddRows: (count?: number) => void;
  onDelete: (idx: number) => void;
  onUpdate: <K extends keyof F21A1>(idx: number, key: K, value: F21A1[K]) => void;
}

const TabDetails: React.FC<TabDetailsProps> = React.memo(
  ({ details, isEditing, onAddRows, onDelete, onUpdate }) => {
    return (
      <div className="detail-panel">
        {isEditing && (
          <div className="detail-toolbar">
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() => onAddRows(10)}
              title="明細新增（+10行）"
            >
              明細新增
            </Button>
          </div>
        )}

        <div className="detail-table-wrapper">
          <table className="detail-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>項次</th>
                <th style={{ width: 100 }}>產品代號</th>
                <th style={{ width: 180 }}>產品名稱</th>
                <th style={{ width: 55 }}>單位</th>
                <th style={{ width: 90 }}>數量</th>
                <th style={{ width: 90 }}>單價</th>
                <th style={{ width: 100 }}>金額</th>
                <th style={{ width: 70 }}>贈量</th>
                <th style={{ width: 80 }}>訂單編號</th>
                <th style={{ width: 70 }}>訂單序號</th>
                <th style={{ width: 100 }}>明細摘要</th>
                {isEditing && <th style={{ width: 40 }}></th>}
              </tr>
            </thead>
            <tbody>
              {details.map((row, idx) => (
                <tr key={idx} className={row.f21a1_11.trim() ? '' : 'empty-row'}>
                  <td className="text-center">{row.f21a1_02}</td>
                  <td>
                    <input
                      className="cell-input"
                      value={row.f21a1_11}
                      maxLength={20}
                      onChange={(e) => onUpdate(idx, 'f21a1_11', e.target.value)}
                      disabled={!isEditing}
                    />
                  </td>
                  <td>
                    {/* auto-fill — read only (BR-D02) */}
                    <input
                      className="cell-input"
                      value={row.f21a1_11a}
                      readOnly
                      disabled
                    />
                  </td>
                  <td>
                    {/* auto-fill — read only */}
                    <input
                      className="cell-input"
                      value={row.f21a1_12}
                      readOnly
                      disabled
                    />
                  </td>
                  <td>
                    <input
                      className="cell-input text-right"
                      type="number"
                      value={row.f21a1_13 || ''}
                      onChange={(e) =>
                        onUpdate(idx, 'f21a1_13', parseFloat(e.target.value) || 0)
                      }
                      disabled={!isEditing}
                    />
                  </td>
                  <td>
                    <input
                      className="cell-input text-right"
                      type="number"
                      value={row.f21a1_14 || ''}
                      onChange={(e) =>
                        onUpdate(idx, 'f21a1_14', parseFloat(e.target.value) || 0)
                      }
                      disabled={!isEditing}
                    />
                  </td>
                  <td>
                    {/* auto-calculated — read only (BR-C01) */}
                    <input
                      className="cell-input text-right"
                      value={
                        row.f21a1_15
                          ? row.f21a1_15.toLocaleString('en-US', { maximumFractionDigits: 2 })
                          : '0'
                      }
                      readOnly
                      disabled
                    />
                  </td>
                  <td>
                    <input
                      className="cell-input text-right"
                      type="number"
                      value={row.f21a1_16 || ''}
                      onChange={(e) =>
                        onUpdate(idx, 'f21a1_16', parseFloat(e.target.value) || 0)
                      }
                      disabled={!isEditing}
                    />
                  </td>
                  <td>
                    <input
                      className="cell-input"
                      value={row.f21a1_21}
                      maxLength={20}
                      onChange={(e) => onUpdate(idx, 'f21a1_21', e.target.value)}
                      disabled={!isEditing}
                    />
                  </td>
                  <td>
                    <input
                      className="cell-input text-right"
                      type="number"
                      value={row.f21a1_22 || ''}
                      onChange={(e) =>
                        onUpdate(idx, 'f21a1_22', parseInt(e.target.value) || 0)
                      }
                      disabled={!isEditing}
                    />
                  </td>
                  <td>
                    <input
                      className="cell-input"
                      value={row.f21a1_18}
                      maxLength={40}
                      onChange={(e) => onUpdate(idx, 'f21a1_18', e.target.value)}
                      disabled={!isEditing}
                    />
                  </td>
                  {isEditing && (
                    <td className="text-center">
                      <Button
                        size="small"
                        danger
                        icon={<MinusOutlined />}
                        onClick={() => onDelete(idx)}
                        title="刪除此行"
                      />
                    </td>
                  )}
                </tr>
              ))}
              {details.length === 0 && (
                <tr>
                  <td
                    colSpan={isEditing ? 12 : 11}
                    className="text-center empty-hint"
                  >
                    {isEditing ? '點擊「明細新增」加入行' : '無明細資料'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="detail-summary">
          共 {details.filter((d) => d.f21a1_11.trim()).length} 筆有效明細
        </div>
      </div>
    );
  }
);

TabDetails.displayName = 'TabDetails';
export default TabDetails;
